import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  ListItemText,
  ListItemIcon,
  List,
  ListItemButton,
} from '@mui/material';
import LazyLoad, { forceCheck } from 'react-lazyload';

const CheckboxList = ({
  options,
  checked,
  setChecked,
  disabled,
  backgroundColor,
  textColor,
}) => {
  const [search, setSearch] = useState('');
  const [scrollParent, setScrollParent] = useState(null);
  const scrollbarWrapperRef = useRef(null);

  useEffect(() => {
    const scrollParentElement = document.querySelector('#checklist-container');
    if (scrollParentElement) {
      setScrollParent(scrollParentElement);
      forceCheck();
    }
  }, [scrollbarWrapperRef]);

  const handleToggle = value => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckAll = () => {
    if (options.length === checked.length) {
      setChecked([]);
    } else {
      setChecked(options.map(opt => opt.value));
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', paddingRight: '42px' }}>
        <Checkbox
          style={{ margin: '0px 24px 0px 4px' }}
          color="primary"
          indeterminate={
            checked.length > 0 && options.length !== checked.length
          }
          disabled={disabled}
          checked={options.length === checked.length}
          disableRipple
          onClick={handleCheckAll}
        />
        <TextField
          label="Search"
          variant="outlined"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setTimeout(() => forceCheck(), 100);
          }}
          fullWidth
          size="small"
          InputLabelProps={{ sx: { color: textColor } }}
          InputProps={{ sx: { color: textColor } }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: textColor,
              },
            },
          }}
        />
      </Box>
      <List
        id="checklist-container"
        ref={scrollbarWrapperRef}
        sx={{
          width: '100%',
          backgroundColor: theme =>
            backgroundColor ?? theme.palette.background.paper,
          maxHeight: 'calc(100vh - 500px)',
          overflow: 'auto',
        }}
      >
        {scrollParent &&
          options
            .filter(option =>
              option.label.toLowerCase().includes(search.toLowerCase()),
            )
            .map(option => {
              const labelId = `checkbox-list-label-${option.value}`;
              return (
                <LazyLoad
                  key={option.value}
                  height={50}
                  offset={450}
                  scrollContainer="#checklist-container"
                  overflow
                >
                  <ListItemButton
                    role={undefined}
                    dense
                    disabled={disabled}
                    onClick={handleToggle(option.value)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        color="primary"
                        edge="start"
                        checked={checked.indexOf(option.value) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={option.label}
                      primaryTypographyProps={{
                        color: textColor ?? 'secondary',
                      }}
                      secondaryTypographyProps={{
                        color: textColor ?? 'secondary',
                      }}
                    />
                  </ListItemButton>
                </LazyLoad>
              );
            })}
      </List>
    </Box>
  );
};

CheckboxList.defaultProps = {
  disabled: false,
};

CheckboxList.propTypes = {
  checked: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  setChecked: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
};

export default CheckboxList;
